import queryString from 'query-string';

import { Country } from '@models/CountriesAndLanguages';
import IngestSummary from '@models/IngestSummary';
import IngestValidation, {
  ValidationCountry,
  ValidationSeverity,
  ValidationTitleType,
} from '@models/IngestValidation';

import { createAdminApi, getApiBaseUrl } from '../shared/utils';

const api = createAdminApi(getApiBaseUrl('ingest'), { 'x-api-version': '1.0' });

export type IngestSource = {
  cancelRequested: boolean;
  deltaInterval: string;
  firstStartAt: string;
  isNotificationsEnabled: boolean;
  isScheduleEnabled: boolean;
  lastSummary?: IngestSummary | null;
  lastSummaryId?: string | null;
  scheduleInterval: string;
  scheduleType: 'delta' | 'full';
  sourceId?: string | null;
  startRequested: boolean;
};

export type IngestSourceInput = Omit<IngestSource, 'lastSummary' | 'lastSummaryId' | 'sourceId'>;

export type IngestSettings = {
  forceSearchUpsert: boolean;
  ingestNextSearchVersion: boolean;
  providerIdPriority: string[];
};

export const fetchIngestSources = () => api.get<(IngestSource | string)[]>('sources');

export const saveIngestSource = (sourceId: string, source: IngestSourceInput) =>
  api.put<IngestSource>(`sources/${sourceId}`, source);

export const deleteIngestSource = (sourceId: string) => api.delete(`sources/${sourceId}`);

export const updateIngestSettings = (settings: IngestSettings) =>
  api.put<IngestSettings>('operations/settings', settings);

export const fetchIngestSettings = () => api.get<IngestSettings>('operations/settings');

export const mergeIngestTitle = (
  sourceMasterId: string,
  targetMasterId: string,
  country: Country
) =>
  api.post('operations/reAggregate/merge/title', {
    sourceMasterId,
    targetMasterId,
    country,
  });

export const moveIngestPlaybackOption = (
  playbackOptionId: string,
  masterId: string | null,
  country: Country
) =>
  api.post('operations/reAggregate/move/playbackOption', {
    playbackOptionId,
    masterId,
    country,
  });

export type IngestDiffKind = 'master' | 'playbackOptions';
export type IngestDiffType = 'diff' | 'missing';

export type IngestPropertyDiff = {
  new: unknown;
  old: unknown;
};

export type IngestDiff = {
  changes?: Record<string, IngestPropertyDiff> | null;
  diffType: IngestDiffType;
  id?: string | null;
  titleType?: string | null;
};

export type FetchIngestDiffsParams = {
  excludeProps?: string[];
  excludeTitleTypes?: ValidationTitleType[];
};

export const fetchIngestDiffs = (
  sourceId: string,
  kind: IngestDiffKind,
  params: FetchIngestDiffsParams = {}
) =>
  api.get<IngestDiff[]>(
    `diffs/sources/${encodeURIComponent(sourceId)}/${kind === 'master' ? 'master-diffs' : 'po-diffs'}`,
    params,
    {
      paramsSerializer: requestParams =>
        queryString.stringify(requestParams, { arrayFormat: 'none' }),
    }
  );

type FetchIngestSummariesParams = {
  skip?: number;
  source?: string;
  take?: number;
};

export const fetchIngestSummaries = (params: FetchIngestSummariesParams = {}) =>
  api.get<IngestSummary[]>('summaries', { skip: 0, take: 1000, ...params });

export const deleteIngestSummary = (summaryId: string) =>
  api.delete(`summaries/${encodeURIComponent(summaryId)}`);

export type FetchIngestValidationsParams = {
  country?: ValidationCountry;
  maxSeverity?: ValidationSeverity;
  playbackOptionIds?: string[];
  providerId?: string;
  skip?: number;
  sourceId?: string;
  summaryId?: string;
  take?: number;
  titleType?: ValidationTitleType;
};

export const fetchIngestValidations = (params: FetchIngestValidationsParams = {}) =>
  api.get<IngestValidation[]>(
    'validations',
    { skip: 0, take: 100, ...params },
    {
      paramsSerializer: requestParams =>
        queryString.stringify(requestParams, { arrayFormat: 'none' }),
    }
  );

export type SqlColumnSchema = {
  dataType?: string | null;
  default?: string | null;
  isNullable: boolean;
  maxLength?: number | null;
  name?: string | null;
  position: number;
};

export type SqlTableSchema = {
  columns?: SqlColumnSchema[] | null;
  name?: string | null;
  tableType?: string | null;
};

export type SqlQueryRow = Record<string, unknown>;

export const fetchSqlSchema = () => api.get<SqlTableSchema[]>('sql/schema');

export const executeSqlQuery = (sql: string) =>
  api.post<SqlQueryRow[]>('sql/query', { sql });
